/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import classNames from 'classnames';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router';

import routeConfiguration from '../../../routeConfiguration';
import { findRouteByRouteName, pathByRouteName } from '../../../util/routes';

export const NamedLinkComponent = ({
  params = {},
  to = {},
  target = '_self',
  children,
  className = '',
  style = {},
  activeClassName = 'NamedLink_active',
  title = null,
  name,
  onClick = () => null,
  ariaLabel = null,
}) => {
  const routes = routeConfiguration();
  const { locale } = useIntl();
  const location = useLocation();

  const onOver = () => {
    const { component: Page } = findRouteByRouteName(name, routes);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  // Link props
  const pathname = pathByRouteName(name, routes, params, locale);
  const active = location.pathname === pathname;

  // <a> element props
  const aElemProps = {
    className: classNames(className, { [activeClassName]: active }),
    style,
    title,
  };

  return (
    <Link
      onClick={onClick}
      onMouseOver={onOver}
      onTouchStart={onOver}
      target={target}
      to={{ pathname, ...to }}
      {...aElemProps}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
};

NamedLinkComponent.displayName = 'NamedLink';

const NamedLink = memo(NamedLinkComponent);

export default NamedLink;
